import { usePoints } from "api/points";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { ExternalLinkTooltipContent } from "components/Tooltips/ExternalLinkTooltip";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useEffect, useState } from "react";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { UNIT_TRANSFER_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { TransferSearchType, UnitTransferModal } from "types/unit-transfers";
import { OLDate } from "utilities/dateFormat";
import { abbreviateNumber } from "utilities/numbers";
import { handleBooleanValue } from "utilities/strings";
import { UnitTransfersModals } from "./UnitTransfersModals";
import { Modals, UnitTransfersModalsActions } from "./helpers";

export function UnitTransfersList() {
  const { message } = useMessage();
  const { notify } = useNotifications();
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const { listing, updateListing } = useListing(UNIT_TRANSFER_LIST_ID);
  const appDispatch = useAppDispatch();
  const tables = useAppSelector(selectTables);
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[UNIT_TRANSFER_LIST_ID]?.filters || [],
  );
  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      UNIT_TRANSFER_LIST_ID,
    );

    setFilters(filters);
  };

  const [open, setOpen] = useState(false);
  const [selectedModal, setModal] = useState<UnitTransferModal>();
  const { usePointsTransferList } = usePoints();
  const { data, isValidating, error } = usePointsTransferList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "createdAt",
    dynamicSearch,
  });
  const headItems = createHeadItems([
    {
      headerText: message({ id: "walletName" }),
      hasMinWidthThreshold: true,
      property: "walletName",
      isSortable: false,
    },
    {
      headerText: message({ id: "walletCode" }),
      property: "walletCode",
      isSortable: false,
    },
    {
      headerText: message({ id: "externalTransferId" }),
      property: "externalTransferId",
      isSortable: false,
      isVisible: false,
    },
    {
      headerText: message({ id: "unit" }),
      property: "unit",
      isSortable: false,
    },
    {
      headerText: message({ id: "type" }),
      property: "type",
      isSortable: true,
    },
    {
      headerText: message({ id: "cancelled" }),
      property: "cancelled",
      isSortable: true,
    },
    {
      headerText: message({ id: "pending" }),
      property: "pending",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "value" }),
      property: "value",
      isSortable: true,
    },
    {
      headerText: message({ id: "comment" }),
      property: "comment",
      isSortable: true,
    },
    {
      headerText: message({ id: "createdOn" }),
      property: "createdAt",
      isSortable: true,
      tooltip: (
        <ExternalLinkTooltipContent
          name="createdOn"
          text={<span>{message({ id: "createdOnInfo" })}</span>}
        />
      ),
    },
    {
      headerText: message({ id: "registeredOn" }),
      property: "registeredOn",
      isSortable: true,
      tooltip: (
        <ExternalLinkTooltipContent
          name="registeredOn"
          text={<span>{message({ id: "registeredOnInfo" })}</span>}
        />
      ),
    },
    {
      headerText: message({ id: "expiresOn" }),
      property: "expiresAt",
      isSortable: true,
    },
    {
      headerText: message({ id: "performedOn" }),
      property: "actionCause:performedAt",
      isSortable: true,
      isVisible: false,
    },
    {
      headerText: message({ id: "expiredOn" }),
      property: "expiredOn",
      isSortable: false,
    },
    {
      headerText: message({ id: "transferID" }),
      property: "transferID",
      isSortable: false,
    },
    {
      headerText: message({ id: "memberID" }),
      property: "memberID",
      isSortable: false,
    },
    {
      headerText: message({ id: "fullName" }),
      property: "fullName",
      isSortable: false,
    },
    {
      headerText: message({ id: "email" }),
      property: "email",
      isSortable: false,
    },
    {
      headerText: message({ id: "phoneNumber" }),
      property: "phoneNumber",
      isSortable: false,
    },
    {
      headerText: message({ id: "loyaltyCardNumber" }),
      property: "loyaltyCardNumber",
      isSortable: false,
    },
    {
      headerText: message({ id: "transactionId" }),
      property: "transactionId",
      isSortable: false,
      isVisible: false,
    },
    {
      headerText: message({ id: "campaignId" }),
      property: "campaignId",
      isSortable: false,
      isVisible: false,
    },
    {
      headerText: message({ id: "customEventId" }),
      property: "customEventId",
      isSortable: false,
      isVisible: false,
    },
    {
      headerText: message({ id: "internalEventName" }),
      property: "internalEventName",
      isSortable: false,
      isVisible: false,
    },
  ]);

  function createItems(items: TransferSearchType[]): Row[] {
    return items.map((item) => ({
      cells: [
        { content: item.walletType?.name, property: "walletName" },
        { content: item.walletType?.code, property: "walletCode" },
        {
          content: item?.externalTransferId,
          property: "externalTransferId",
        },
        { content: item.walletType?.unitPluralName, property: "unit" },
        {
          content: item.type,
          property: "type",
        },
        {
          content: message({ id: handleBooleanValue(!!item.cancelled) }),
          property: "cancelled",
        },
        {
          content: message({ id: handleBooleanValue(!!item.pending) }),
          property: "pending",
        },
        {
          align: "right",
          content: item.value,
          emptyString: "0",
          property: "value",
        },
        { content: item.comment, property: "comment", dataHjSuppress: true },
        { content: OLDate(item.createdAt), property: "createdAt" },
        {
          content: OLDate(item.registeredOn),
          property: "registeredOn",
        },
        { content: OLDate(item.expiresAt), property: "expiresAt" },
        { content: OLDate(item.expiredAt), property: "expiredOn" },
        {
          content: OLDate(item.actionCause?.performedAt),
          property: "actionCause:performedAt",
        },
        { content: item.transferId, property: "transferID" },
        {
          content: item.member?.id,
          property: "memberID",
          nonClickable: true,
          onDirectClick: () => {
            if (!item.member.id) return;
            navigator.clipboard.writeText(item.member.id).then(() => {
              notify(
                `${message({ id: "memberID" })} ${message({ id: "copied" })}`,
              );
            });
          },
          dataHjSuppress: true,
        },

        {
          content:
            item.member?.firstName && item.member?.lastName
              ? `${item.member.firstName} ${item.member.lastName}`
              : "-",
          property: "fullName",
          dataHjSuppress: true,
        },
        {
          content: item.member?.email,
          property: "email",
          dataHjSuppress: true,
        },
        {
          content: item.member?.phoneNumber,
          property: "phoneNumber",
          dataHjSuppress: true,
        },
        {
          content: item.member?.loyaltyCardNumber,
          property: "loyaltyCardNumber",
          dataHjSuppress: true,
        },
        { content: item.actionCause.transactionId, property: "transactionId" },
        { content: item.actionCause.campaignId, property: "campaignId" },
        { content: item.actionCause.customEventId, property: "customEventId" },
        {
          content: item.actionCause.internalEventName,
          property: "internalEventName",
        },
      ],
      id: item.transferId,
      metadata: {
        cancelled: item.cancelled,
        pending: item.pending,
        type: item.type,
        expiresAt: item.expiresAt,
      },
      testId: "unit-transfers-row",
    }));
  }
  const items = data?.items ? createItems(data.items) : [];

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, UNIT_TRANSFER_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, data, error]);

  return (
    <>
      <TopHeader title={message({ id: "unitTransfers" })} />
      <Subheader>
        <FiltersV2
          donutLabel={message({ id: "unitTransfers" })}
          properties={[
            {
              query: "pending",
              label: message({ id: "pending" }),
              type: "boolean-yes-no",
            },
            {
              query: "cancelled",
              label: message({ id: "cancelled" }),
              type: "boolean-yes-no",
            },
            {
              query: "actionCause:campaignId",
              label: message({ id: "campaignId" }),
              type: "text",
            },
            {
              query: "actionCause:campaignName",
              label: message({ id: "campaignName" }),
              type: "campaign-name-filter",
            },
            {
              query: "comment",
              label: message({ id: "comment" }),
              type: "text",
            },
            {
              query: "createdAt",
              label: message({ id: "createdOn" }),
              type: "datetime",
            },
            {
              query: "actionCause:customEventId",
              label: message({ id: "customEventId" }),
              type: "text",
            },
            {
              query: "member:email",
              label: message({ id: "email" }),
              type: "member-email-filter",
            },
            {
              query: "expiresAt",
              label: message({ id: "expiresOn" }),
              type: "datetime",
            },
            {
              query: "actionCause:internalEventName",
              label: message({ id: "internalEventName" }),
              type: "text",
            },
            {
              query: "lockedUntil",
              label: message({ id: "lockedUntil" }),
              type: "datetime",
            },
            {
              query: "member:loyaltyCardNumber",
              label: message({ id: "loyaltyCardNumber" }),
              type: "member-loyalty-card-number",
            },
            {
              query: "member:id",
              label: message({ id: "memberID" }),
              type: "text",
            },
            {
              query: "member:phoneNumber",
              label: message({ id: "phoneNumber" }),
              type: "member-phone-filter",
            },
            {
              query: "actionCause:transactionId",
              label: message({ id: "transactionId" }),
              type: "text",
            },
            {
              query: "transferId",
              label: message({ id: "transferID" }),
              type: "text",
            },
            {
              query: "type",
              label: message({ id: "type" }),
              type: "points-transfers-types",
            },
            {
              query: "value",
              label: message({ id: "value" }),
              type: "number",
            },
            {
              query: "walletType:code",
              label: message({ id: "walletCode" }),
              type: "wallet-type-code-filter",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={data?.total}
        />
        <SubheaderWrapper alignRight>
          <SubheaderMenuButton
            data-test-id="actions"
            disabled={isValidating || data?.total.filtered === 0}
            disabledInfo={{
              name: message({ id: "massActionsAreUnavailable" }),
              text: message({ id: "massActionsAreUnavailableInfo" }),
            }}
            menuItems={[
              {
                text: message({ id: "activateFilteredTransfers" }),
                onClick: () => {
                  setModal({ transfer: "", mode: Modals.ActivateTransfers });
                  setOpen(true);
                },
              },
              {
                text: message({ id: "cancelFilteredTransfers" }),
                onClick: () => {
                  setModal({ transfer: "", mode: Modals.CancelTransfers });
                  setOpen(true);
                },
              },
              {
                text: message({ id: "expireFilteredTransfers" }),
                onClick: () => {
                  setModal({ transfer: "", mode: Modals.ExpireTransfers });
                  setOpen(true);
                },
              },
            ]}
            secondary
            text={`${message({ id: "actions" }).toLocaleUpperCase()} (${abbreviateNumber(data?.total.filtered)})`}
          />
          <SubheaderMenuButton
            text={message({ id: "unitTransfers" })}
            data-test-id="unit-transfers-list"
            menuItems={[
              {
                onClick: () => {
                  setModal({ transfer: "", mode: Modals.Add });
                  setOpen(true);
                },
                text: message({ id: "addUnitsTransfer" }),
              },
              {
                onClick: () => {
                  setModal({ transfer: "", mode: Modals.Deduct });
                  setOpen(true);
                },
                text: message({ id: "deductUnitsTransfer" }),
              },
              {
                onClick: () => {
                  setModal({
                    transfer: "",
                    mode: Modals.ImportAddingUnitsTransfers,
                  });
                  setOpen(true);
                },
                text: message(
                  { id: "importTransfersType" },
                  {
                    importType: message({
                      id: "add",
                    }).toLocaleLowerCase(),
                  },
                ),
              },
              {
                onClick: () => {
                  setModal({
                    transfer: "",
                    mode: Modals.ImportSpendingUnitsTransfers,
                  });
                  setOpen(true);
                },
                text: message(
                  { id: "importTransfersType" },
                  {
                    importType: message({
                      id: "deduct",
                    }).toLocaleLowerCase(),
                  },
                ),
              },
            ]}
          />
        </SubheaderWrapper>
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          <DynamicSearch
            property="member:email"
            setDynamicSearch={setDynamicSearch}
            properties={[
              { id: "member:email", label: "email" },
              {
                id: "member:phoneNumber",
                label: "phone",
              },
              { id: "member:loyaltyCardNumber", label: "loyaltyCardNumber" },
              {
                id: "walletType:code",
                label: "walletName",
              },
            ]}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: UNIT_TRANSFER_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={UNIT_TRANSFER_LIST_ID}
        data-test-id="unit-transfers"
        onRowClick={(id: string) => {
          setModal({ transfer: id as string, mode: Modals.Details });
          setOpen(true);
        }}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "createdAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: UnitTransfersModalsActions({ setOpen, setModal }),
        }}
        columns={data && headItems}
        rows={data && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
        }}
        clearFilters={() => setFilters([])}
      />
      <UnitTransfersModals
        open={open}
        selectedModal={selectedModal}
        close={() => setOpen(false)}
        setModal={(modal: UnitTransferModal) => setModal(modal)}
        itemsLength={data?.total.filtered}
        filters={filters}
        dynamicSearchFilter={dynamicSearch}
      />
    </>
  );
}
